import { put, takeLatest, call } from 'redux-saga/effects'
import { FORM_ERROR } from 'final-form'
import fetchFromAPI, { getDefaultAutorizedHeaders } from 'helpers/fetchFromApi'
import UrlHelper from 'helpers/url'
import NoticeHelper from 'helpers/notice'
import * as ApiRouter from 'constants/apiRouting'
import {
  API_URL_LICENSEE_LIST,
  API_URL_ROLES_LIST,
  API_URL_USER_INVITATION,
  API_URL_USER_SET_PASSWORD,
  RESET_STORE,
} from 'constants/index'
import RoleHelper from 'helpers/role'
import Psfs from 'lib/psfs/Psfs'
import { SHOW_NOTICE } from '../notice'
import {
  USER_FETCH_TABLE_DATA,
  USER_SET_TABLE_DATA,
  USER_CHANGE,
  USER_DELETE,
  USER_INVITATION,
  USER_INVITATION_SUCCESS,
  USER_INVITATION_FAILURE,
  USER_SET_PASSWORD,
  USER_SET_LOADING,
  USER_DELETE_OWN_ACCOUNT,
  FETCH_USER_FORM_DATA,
  FETCH_USER_FORM_DATA_SUCCESS,
  FETCH_USER_FORM_DATA_FAILURE,
} from './reducer'

function* userFetchTableData(action) {
  try {
    yield put({ type: USER_SET_LOADING, payload: true })

    const data = yield call(fetchFromAPI, {
      headers: getDefaultAutorizedHeaders(),
      partUrl: `${ApiRouter.USER_TABLE}${UrlHelper.getPsfsQuery(
        action.payload,
      )}`,
    })

    yield put({
      type: USER_SET_TABLE_DATA,
      tableData: data,
    })

    yield put({ type: USER_SET_LOADING, payload: false })
  } catch (error) {
    console.log('USERS INTERFACE ERROR', error)
  }
}

function* userGetFormData({ payload: { entityId, roleLevel } }) {
  try {
    const formData = {
      userGroups: [],
      roles: {},
      user: null,
    }

    formData.roles = yield call(fetchFromAPI, {
      headers: getDefaultAutorizedHeaders(),
      partUrl: API_URL_ROLES_LIST,
    })

    if (entityId) {
      formData.user = yield call(fetchFromAPI, {
        headers: getDefaultAutorizedHeaders(),
        partUrl: UrlHelper.create(ApiRouter.USER_INFO, {
          id: entityId,
        }),
      })
    }

    if (roleLevel >= RoleHelper.ROLE_SUPERADMIN_LEVEL) {
      const psfs = Psfs.create(API_URL_LICENSEE_LIST)
        .createPagination(1, 'all')
        .addFields(['id', 'name'])

      formData.licensees = yield call(fetchFromAPI, {
        headers: getDefaultAutorizedHeaders(),
        partUrl: `${API_URL_LICENSEE_LIST}${UrlHelper.getPsfsQuery({ psfs })}`,
      })
    }

    yield put({ type: FETCH_USER_FORM_DATA_SUCCESS, formData })
  } catch (error) {
    console.log('USERS INTERFACE ERROR', error)
    yield put({ type: FETCH_USER_FORM_DATA_FAILURE })
  }
}

function* userChange({ payload: { entityId, values }, promise }) {
  try {
    const response = yield call(fetchFromAPI, {
      headers: getDefaultAutorizedHeaders(),
      method: !entityId ? 'POST' : 'PUT',
      partUrl: !entityId
        ? ApiRouter.USERS
        : UrlHelper.create(ApiRouter.USER_INFO, {
            id: entityId,
          }),
      data: values,
    })

    if (response.error) {
      if (response.errors.site) {
        yield put({
          type: SHOW_NOTICE,
          notice:
            response.error && NoticeHelper.createErrorObj(response.errors.site),
        })
      }

      return yield call(promise.resolve, response.errors)
    }

    yield put({
      type: SHOW_NOTICE,
      notice: NoticeHelper.createSuccessObj(
        `User ${entityId === 0 ? 'create' : 'change'} successful`,
      ),
    })

    return yield call(promise.resolve, response.error)
  } catch (error) {
    throw new Error(`UNKNOWN USER CHANGE ERROR. DATA: ${error}`)
  }
}

function* sendInvitation(action) {
  try {
    const value = yield call(fetchFromAPI, {
      headers: getDefaultAutorizedHeaders(),
      partUrl: UrlHelper.create(API_URL_USER_INVITATION, action.payload),
    })

    if (!value || !value.error) {
      yield put({ type: USER_INVITATION_SUCCESS, payload: value })
      yield put({
        type: SHOW_NOTICE,
        notice: NoticeHelper.createSuccessObj(value.message),
      })
    } else {
      yield put({ type: USER_INVITATION_FAILURE, error: value.errors })
    }
  } catch (error) {
    yield put({ type: USER_INVITATION_FAILURE, error: error.toString() })
  }
}

function* setPassword(action) {
  const {
    payload: { new_password, confirm_password, id },
    promise,
  } = action

  try {
    const response = yield call(fetchFromAPI, {
      data: {
        new_password,
        confirm_password,
      },
      headers: getDefaultAutorizedHeaders(),
      method: 'PUT',
      partUrl: UrlHelper.create(API_URL_USER_SET_PASSWORD, { id }),
    })

    const { error, errors, message } = response || {}

    if (!response || !error) {
      yield put({
        type: SHOW_NOTICE,
        notice: NoticeHelper.createSuccessObj(message),
      })

      return yield call(promise.resolve)
    }

    yield call(promise.resolve, {
      error: true,
      [FORM_ERROR]: errors,
      errorString: errors,
    })
  } catch (error) {
    console.error(error.toString())
  }
}

function* userDelete(action) {
  try {
    const response = yield call(fetchFromAPI, {
      headers: getDefaultAutorizedHeaders(),
      method: 'DELETE',
      partUrl: UrlHelper.create(ApiRouter.USER_INFO, {
        id: action.payload.entityId,
      }),
    })

    if (response === 204) {
      yield put({
        type: SHOW_NOTICE,
        notice: NoticeHelper.createSuccessObj('User delete successful'),
      })

      return yield userFetchTableData(action)
    }

    console.log('USER DELETE UNKNOWN ERROR. RESPONSE: ', response)
  } catch (error) {
    console.log('USER INTERFACE ERROR', error)
  }
}

function* userDeleteOwnAccount({ payload: { redirect } }) {
  try {
    const response = yield call(fetchFromAPI, {
      headers: getDefaultAutorizedHeaders(),
      method: 'DELETE',
      partUrl: ApiRouter.PROFILE,
    })

    if (response === 204) {
      yield put({ type: RESET_STORE })
      yield redirect(ApiRouter.LOGIN)
    } else {
      console.log('USER DELETE UNKNOWN ERROR. RESPONSE: ', response)
    }
  } catch (error) {
    console.log('USER INTERFACE ERROR', error)
  }
}

function* userSaga() {
  yield takeLatest(USER_FETCH_TABLE_DATA, userFetchTableData)
  yield takeLatest(FETCH_USER_FORM_DATA, userGetFormData)
  yield takeLatest(USER_CHANGE, userChange)
  yield takeLatest(USER_INVITATION, sendInvitation)
  yield takeLatest(USER_DELETE, userDelete)
  yield takeLatest(USER_DELETE_OWN_ACCOUNT, userDeleteOwnAccount)
  yield takeLatest(USER_SET_PASSWORD, setPassword)
}

export default userSaga
